// useAutomationRuleModalState.js

import { useCallback, useEffect, useState } from 'react';

import {
  buildConditionGroupsFromRows,
  buildDefaultAction,
  buildDefaultCondition,
  buildDefaultConditionGroup,
  flattenConditionGroups,
  normalizeAutomationTriggerType,
  normalizeJoinToNext,
} from '../utils/automationRuleUtils';

/**
 * useAutomationRuleModalState
 *
 * Manages draft lifecycle, condition/action mutations, and save flows for automation rule editors.
 */
export default function useAutomationRuleModalState({
  draft: controlledDraft,
  fieldTypeByKey,
  isOpen = true,
  onClose,
  onDelete,
  onDraftChange,
  onSave,
  rule,
  ruleFieldOptions,
}) {
  const isControlled = controlledDraft !== undefined && typeof onDraftChange === 'function';
  const [internalDraftRule, setInternalDraftRule] = useState(null);

  useEffect(() => {
    if (isControlled || !isOpen) return;
    setInternalDraftRule(rule || null);
  }, [isControlled, isOpen, rule]);

  const draftRule = isControlled ? controlledDraft : internalDraftRule;

  const setDraftRule = useCallback((updater) => {
    if (isControlled) {
      const nextDraft = typeof updater === 'function'
        ? updater(controlledDraft)
        : updater;
      onDraftChange(nextDraft);
      return;
    }

    setInternalDraftRule(updater);
  }, [controlledDraft, isControlled, onDraftChange]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const updateRule = useCallback((ruleKey, updater) => {
    setDraftRule((prev) => {
      if (!prev?.key || prev.key !== ruleKey) return prev;
      return updater(prev);
    });
  }, [setDraftRule]);

  const updateConditionRows = useCallback((ruleKey, updater) => {
    updateRule(ruleKey, (prev) => {
      const allowEmpty = normalizeAutomationTriggerType(prev?.trigger?.type) === 'record_created';
      const triggerFieldType = fieldTypeByKey.get(prev?.trigger?.fieldKey || '') || '';
      const defaultFieldKey = String(
        triggerFieldType === 'people'
          ? (ruleFieldOptions[0]?.key || '')
          : (prev?.trigger?.fieldKey || ruleFieldOptions[0]?.key || ''),
      ).trim();
      const fallbackGroups = Array.isArray(prev?.conditionGroups) && prev.conditionGroups.length
        ? prev.conditionGroups
        : (allowEmpty ? [] : [buildDefaultConditionGroup({ defaultFieldKey })]);
      const currentRows = flattenConditionGroups(fallbackGroups);
      const nextRowsRaw = updater(currentRows);
      const nextRows = Array.isArray(nextRowsRaw) ? nextRowsRaw : currentRows;
      const nextGroups = buildConditionGroupsFromRows(nextRows, {
        defaultFieldKey,
        allowEmpty,
      });

      return {
        ...prev,
        conditionGroups: nextGroups,
        conditions: nextGroups[0]?.conditions || (allowEmpty ? [] : [buildDefaultCondition({ defaultFieldKey })]),
      };
    });
  }, [fieldTypeByKey, ruleFieldOptions, updateRule]);

  const handleAddCondition = useCallback((ruleKey) => {
    updateConditionRows(ruleKey, (rows) => {
      const baseRows = Array.isArray(rows) ? rows : [];
      const fallbackFieldKey = String(ruleFieldOptions[0]?.key || '').trim();
      return [
        ...baseRows.map((row, index) => ({
          ...row,
          joinToNext: index === baseRows.length - 1
            ? normalizeJoinToNext(row?.joinToNext)
            : row?.joinToNext,
        })),
        {
          ...buildDefaultCondition({
            defaultFieldKey: baseRows[0]?.fieldKey || fallbackFieldKey,
          }),
          joinToNext: 'and',
        },
      ];
    });
  }, [ruleFieldOptions, updateConditionRows]);

  const handleRemoveCondition = useCallback((ruleKey, conditionKey) => {
    updateConditionRows(ruleKey, (rows) => rows.filter((row) => row.key !== conditionKey));
  }, [updateConditionRows]);

  const handleAddAction = useCallback((ruleKey) => {
    updateRule(ruleKey, (prev) => ({
      ...prev,
      actions: [
        ...(Array.isArray(prev.actions) ? prev.actions : []),
        buildDefaultAction({
          databaseKey: prev?.scope?.databaseKey,
          typeKey: prev?.scope?.nodeTypeKey,
        }),
      ],
    }));
  }, [updateRule]);

  const handleRemoveAction = useCallback((ruleKey, actionKey) => {
    updateRule(ruleKey, (prev) => {
      const currentActions = Array.isArray(prev.actions) ? prev.actions : [];
      const nextActions = currentActions.filter((action) => action.key !== actionKey);
      return {
        ...prev,
        actions: nextActions.length ? nextActions : currentActions,
      };
    });
  }, [updateRule]);

  const handleSave = useCallback(async () => {
    if (!draftRule) return;

    const didSave = await onSave?.(draftRule);
    if (didSave) {
      handleClose();
    }
  }, [draftRule, handleClose, onSave]);

  const handleDelete = useCallback(async () => {
    const ruleKey = String(draftRule?.key || '').trim();
    if (!ruleKey) return;

    const didDelete = await onDelete?.(ruleKey);
    if (didDelete) {
      handleClose();
    }
  }, [draftRule?.key, handleClose, onDelete]);

  return {
    handlers: {
      handleAddAction,
      handleAddCondition,
      handleClose,
      handleDelete,
      handleRemoveAction,
      handleRemoveCondition,
      handleSave,
      updateConditionRows,
      updateRule,
    },
    modalState: {
      draftRule,
    },
  };
}
